import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, LogOut } from "lucide-react";
import {
  passkeysKeys,
  passkeysMutationKeys,
  pendingPasskeyRecoveryCodesKey,
  recoveryCodesWarningActive,
  setRecoveryCodesAtRisk,
  usePasskeys,
  usePendingPasskeyRecoveryCodes,
  useRecoveryCodesAtRisk,
} from "../hooks/usePasskeys";
import { pendingTotpRecoveryCodesKey, usePendingTotpRecoveryCodes } from "../hooks/useTotp";
import {
  createMfaRecoveryHoldStore,
  createPasskeysSectionPresenceStore,
  MfaRecoveryHoldContext,
  PasskeysSectionPresenceContext,
  useMfaRecoveryHoldStore,
  type MfaRecoveryHoldSource,
} from "../mfa-recovery-hold";
import {
  persistCodesHoldReleased,
  readPersistedCodesHoldReleased,
} from "../recovery-codes-at-risk";
import { announceGateLift, clearGateLiftAnnouncement } from "../gate-lift";
import { useSignInAgain } from "../hooks/useSignInAgain";
import { DeleteAccountDialog } from "./DeleteAccountDialog";
import { RecoveryCodesAtRiskBanner } from "./RecoveryCodesAtRiskBanner";
import { EnrolOutcomeNotice } from "./EnrolOutcomeNotice";
import { PasskeysSection } from "./PasskeysSection";
import { TotpSection } from "./TotpSection";
import { useAuth } from "@/providers/auth-provider";
import { isSessionEndedError } from "@/lib/api/client";
import { useNetworkOnline } from "@/hooks/useNetworkOnline";
import { useAnyDialogOpen } from "@/components/ui/Dialog";
import { PageLoader } from "@/components/ui/PageLoader";
import type { ReactNode, RefObject } from "react";
import type { PasskeyListResponse } from "../api/passkeys.api";

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

interface MfaSetupGateProps {
  children: ReactNode;
}

function useHeldHeadingFocus(
  active: boolean,
  dialogOpen: boolean,
  ref: RefObject<HTMLHeadingElement | null>,
) {
  const deliveredRef = useRef(false);
  useEffect(() => {
    if (!active) {
      deliveredRef.current = false;
      return;
    }
    const heading = ref.current;
    if (deliveredRef.current || dialogOpen || heading === null) return;
    heading.focus();
    deliveredRef.current = document.activeElement === heading;
  }, [active, dialogOpen, ref]);
}

function readServerDeadline(data: PasskeyListResponse | undefined) {
  if (data === undefined || data.mfaSetupOverdue === undefined) {
    return { known: false, deadline: null, overdue: false };
  }

  return {
    known: true,
    deadline: data.mfaSetupDeadline ?? null,
    overdue: data.mfaSetupOverdue === true,
  };
}

export function MfaSetupGate({ children }: MfaSetupGateProps) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [timerGen, setTimerGen] = useState(0);
  const deadlineRetryRef = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const unconfirmedHeadingRef = useRef<HTMLHeadingElement>(null);
  const signInAgain = useSignInAgain("/account");
  const [liftSeen, setLiftSeen] = useState(false);
  const [liftMessage, setLiftMessage] = useState<string | null>(null);
  const [seenBlocked, setSeenBlocked] = useState(false);
  const regionRef = useRef<HTMLParagraphElement>(null);
  const [seenUnconfirmedError, setSeenUnconfirmedError] = useState(false);
  const [holdStore] = useState(createMfaRecoveryHoldStore);
  const holds = useMfaRecoveryHoldStore(holdStore);
  const [presenceStore] = useState(createPasskeysSectionPresenceStore);
  const passkeysSectionMounted = useSyncExternalStore(
    presenceStore.subscribe,
    presenceStore.getSnapshot,
  );
  const { data, error, refetch, isFetching, isPaused } = usePasskeys();
  const sessionEnded = isSessionEndedError(error);
  const online = useNetworkOnline();
  const localRecoveryCodesAtRisk = useRecoveryCodesAtRisk();
  const recoveryCodesAtRisk = recoveryCodesWarningActive(localRecoveryCodesAtRisk, data);
  const pendingTotpCodes = usePendingTotpRecoveryCodes();
  const pendingPasskeyCodes = usePendingPasskeyRecoveryCodes();
  const anyDialogOpen = useAnyDialogOpen();

  const mfaConfirmedOff = data !== undefined && data.mfaEnabled === false;

  const server = readServerDeadline(data);
  const required = server.known && server.deadline !== null;
  const deadline = server.deadline;
  const enforceable = required && mfaConfirmedOff;
  const blocked = server.known && server.overdue;

  const reportHold = useCallback(
    (source: MfaRecoveryHoldSource, open: boolean) => {
      holdStore.set(source, open);
    },
    [holdStore],
  );

  const regeneratingCodes = useIsMutating({ mutationKey: passkeysMutationKeys.regenerate }) > 0;
  const [codesHoldReleased, setCodesHoldReleased] = useState(readPersistedCodesHoldReleased);
  const [releasedThisLoad, setReleasedThisLoad] = useState(false);
  if (
    (!recoveryCodesAtRisk || data?.mfaEnabled === false) &&
    (codesHoldReleased || releasedThisLoad)
  ) {
    setCodesHoldReleased(false);
    setReleasedThisLoad(false);
  }
  const releaseCodesHold = () => {
    setCodesHoldReleased(true);
    setReleasedThisLoad(true);
    persistCodesHoldReleased();
  };
  const listUnconfirmed = recoveryCodesAtRisk && data === undefined && !codesHoldReleased;
  const codesAtRisk = recoveryCodesAtRisk && data?.mfaEnabled === true;
  const codesMayBeAtRisk = recoveryCodesAtRisk && !mfaConfirmedOff;
  const codesAtRiskHold = codesAtRisk && !codesHoldReleased;
  const freeze =
    holds.passkey ||
    holds.totp ||
    deleteOpen ||
    codesAtRiskHold ||
    regeneratingCodes ||
    data === undefined;
  const wantBlock = enforceable && blocked;
  const [pinnedBlock, setPinnedBlock] = useState(wantBlock);
  if (!freeze && pinnedBlock !== wantBlock) {
    setPinnedBlock(wantBlock);
  }
  const displayBlock = freeze ? pinnedBlock : wantBlock;
  const codesUnchecked = codesMayBeAtRisk && data === undefined;
  const codesCheckBusy = isFetching || isPaused;
  const codesCarrierVisible = codesMayBeAtRisk && !listUnconfirmed && !displayBlock;
  const [atRiskBefore, setAtRiskBefore] = useState(recoveryCodesAtRisk);
  const [raisedThisLoad, setRaisedThisLoad] = useState(false);
  const raisedNow = recoveryCodesAtRisk !== atRiskBefore ? recoveryCodesAtRisk : raisedThisLoad;
  if (recoveryCodesAtRisk !== atRiskBefore) {
    setAtRiskBefore(recoveryCodesAtRisk);
    setRaisedThisLoad(recoveryCodesAtRisk);
  }
  const [carrierWasVisible, setCarrierWasVisible] = useState(codesCarrierVisible);
  const [announcePending, setAnnouncePending] = useState(false);
  const [announceShown, setAnnounceShown] = useState(false);
  if (codesCarrierVisible !== carrierWasVisible) {
    setCarrierWasVisible(codesCarrierVisible);
    setAnnouncePending(codesCarrierVisible && raisedNow && !releasedThisLoad);
    setAnnounceShown(false);
  }
  const announceCarrier = announcePending && !anyDialogOpen;
  if (announceCarrier && !announceShown) {
    setAnnounceShown(true);
  }
  if (announcePending && anyDialogOpen && announceShown) {
    setAnnouncePending(false);
    setAnnounceShown(false);
  }
  const blockUnchecked = displayBlock && codesUnchecked;
  const blockWasUncheckedRef = useRef(false);
  const listWaitingForNetwork = data === undefined && !online;
  const [carrierRetryFailed, setCarrierRetryFailed] = useState(false);
  if (carrierRetryFailed && (!codesUnchecked || listWaitingForNetwork)) {
    setCarrierRetryFailed(false);
  }
  const [listRetryPressedOn, setListRetryPressedOn] = useState<"overlay" | "gate" | null>(null);
  const announceOverlayWaiting = listRetryPressedOn === "overlay" && listWaitingForNetwork;
  const announceGateWaiting = listRetryPressedOn === "gate" && listWaitingForNetwork;
  const checkListAgain = (surface: "overlay" | "gate") => {
    setListRetryPressedOn(surface);
    return refetch().then((result) => {
      setListRetryPressedOn(null);
      return result;
    });
  };
  const retryCodesCheck = () => {
    void checkListAgain("gate").then((result) => {
      setCarrierRetryFailed(result.isError);
    });
  };
  const codesSessionEnded = codesUnchecked && sessionEnded;
  const codesWaitingShown = listWaitingForNetwork && !codesSessionEnded;
  const announceCodesSessionEnded = codesSessionEnded && carrierRetryFailed;

  if (!isAuthenticated) {
    if (seenBlocked) setSeenBlocked(false);
    if (liftSeen) setLiftSeen(false);
    if (liftMessage !== null) setLiftMessage(null);
  } else if (displayBlock && !seenBlocked) {
    setSeenBlocked(true);
    if (liftSeen) setLiftSeen(false);
    if (liftMessage !== null) setLiftMessage(null);
  } else if (!displayBlock && seenBlocked) {
    setSeenBlocked(false);
    setLiftSeen(true);
  }

  const listUnconfirmedError = listUnconfirmed && error !== null;
  if (!isAuthenticated) {
    if (seenUnconfirmedError) setSeenUnconfirmedError(false);
  } else if (listUnconfirmedError && !seenUnconfirmedError) {
    setSeenUnconfirmedError(true);
  }

  useHeldHeadingFocus(displayBlock && !listUnconfirmed, anyDialogOpen, headingRef);

  useEffect(() => {
    const wasUnchecked = blockWasUncheckedRef.current;
    blockWasUncheckedRef.current = blockUnchecked;
    if (!wasUnchecked || blockUnchecked || !displayBlock) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    headingRef.current?.focus();
  }, [blockUnchecked, displayBlock]);

  const uncovered = !displayBlock && !listUnconfirmed;
  const wasCovered = liftSeen || releasedThisLoad || seenUnconfirmedError;
  const covered = displayBlock || listUnconfirmed;
  if (isAuthenticated && uncovered && wasCovered && liftMessage === null) {
    setLiftMessage(t("account.mfaGate.restored"));
  } else if (covered && liftMessage !== null) {
    setLiftMessage(null);
  }
  useEffect(() => {
    if (covered) clearGateLiftAnnouncement(regionRef.current);
  }, [covered]);
  useEffect(() => {
    if (liftMessage === null) return;
    announceGateLift(liftMessage, regionRef.current);
  }, [liftMessage]);

  useHeldHeadingFocus(listUnconfirmedError, anyDialogOpen, unconfirmedHeadingRef);

  useEffect(() => {
    if (!enforceable || blocked || deadline == null) {
      deadlineRetryRef.current = 0;
      return;
    }
    const delay = deadline - Date.now();
    if (delay <= 0) {
      void queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
      if (deadlineRetryRef.current >= 6) return;
      const id = setTimeout(() => {
        deadlineRetryRef.current += 1;
        setTimerGen((g) => g + 1);
      }, 5_000);
      return () => clearTimeout(id);
    }
    deadlineRetryRef.current = 0;
    if (delay > MAX_TIMEOUT_MS) {
      const id = setTimeout(() => setTimerGen((g) => g + 1), MAX_TIMEOUT_MS);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
      setTimerGen((g) => g + 1);
    }, delay);
    return () => clearTimeout(id);
  }, [enforceable, blocked, deadline, queryClient, timerGen]);

  useEffect(() => {
    if (!enforceable || blocked || deadline == null) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && deadline <= Date.now()) {
        void queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [enforceable, blocked, deadline, queryClient]);

  const handleLogout = () => {
    logout();
    navigate({ to: "/login" });
  };

  const deadlineNoticeShown = isAuthenticated && enforceable && !displayBlock && !bannerDismissed;
  const deadlineNotice =
    deadline !== null
      ? t("account.mfaGate.banner", {
          date: new Date(deadline).toLocaleDateString(i18n.language),
        })
      : t("account.mfaGate.bannerNoDate");

  let body: ReactNode = null;
  if (isAuthenticated) {
    if (!enforceable && !displayBlock) {
      body = <>{children}</>;
    } else if (!displayBlock) {
      body = (
        <>
          {children}
          {!bannerDismissed && (
            <div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-warning/30 bg-surface p-4 shadow-lg">
              <p className="flex items-start gap-2 text-xs text-heading">
                <ShieldAlert size={16} className="mt-0.5 shrink-0 text-warning" />
                <span>{deadlineNotice}</span>
              </p>
              <div className="mt-3 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setBannerDismissed(true)}
                  className="text-xs font-medium text-muted transition-colors hover:text-heading"
                >
                  {t("common.dismiss")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBannerDismissed(true);
                    navigate({ to: "/account" });
                  }}
                  className="text-xs font-semibold text-indigo transition-colors hover:text-indigo-bright"
                >
                  {t("account.mfaGate.setUpNow")}
                </button>
              </div>
            </div>
          )}
        </>
      );
    } else {
      body = (
        <main
          aria-labelledby={headingId}
          className="min-h-screen overflow-y-auto bg-void px-4 py-10 outline-none"
        >
          <div className="mx-auto max-w-2xl">
            <div className="text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-warning/10">
                <ShieldAlert size={28} className="text-warning" />
              </div>
              <h1
                ref={headingRef}
                id={headingId}
                tabIndex={-1}
                className="mt-4 text-2xl font-bold text-heading outline-none"
              >
                {codesUnchecked
                  ? t("account.mfaGate.codesUnconfirmedTitle")
                  : codesMayBeAtRisk
                    ? t("account.mfaGate.codesTitle")
                    : t("account.mfaGate.title")}
              </h1>
              <p
                key={announceCodesSessionEnded ? "codes-block-body-announced" : "codes-block-body"}
                id="codes-block-body"
                role={announceCodesSessionEnded ? "alert" : undefined}
                className="mt-2 text-sm text-muted"
              >
                {codesSessionEnded
                  ? t("account.mfaGate.codesSessionEndedBody")
                  : codesUnchecked
                    ? t("account.mfaGate.codesUnconfirmedBody")
                    : codesMayBeAtRisk
                      ? t("account.mfaGate.codesSubtitle")
                      : t("account.mfaGate.subtitle")}
              </p>
              {!codesMayBeAtRisk && (
                <p className="mt-2 text-sm text-muted">{t("account.mfaGate.recoveryHint")}</p>
              )}
              {codesUnchecked && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (codesSessionEnded) signInAgain();
                      else if (!codesCheckBusy) retryCodesCheck();
                    }}
                    aria-describedby={
                      codesWaitingShown
                        ? "codes-block-body codes-block-waiting"
                        : "codes-block-body"
                    }
                    aria-disabled={codesCheckBusy || undefined}
                    aria-busy={(codesCheckBusy && !codesWaitingShown) || undefined}
                    className="mt-4 inline-flex h-10 items-center justify-center rounded-lg bg-indigo px-6 text-sm font-semibold text-white transition-all hover:bg-indigo-bright aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                  >
                    {codesSessionEnded ? t("account.staleSession.action") : t("common.retry")}
                  </button>
                  {codesWaitingShown && (
                    <p
                      key={announceGateWaiting && !listUnconfirmed ? "announced" : "quiet"}
                      id="codes-block-waiting"
                      role={announceGateWaiting && !listUnconfirmed ? "alert" : undefined}
                      className="mt-2 text-sm text-muted"
                    >
                      {t("common.waitingForNetwork")}
                    </p>
                  )}
                  {carrierRetryFailed && !codesCheckBusy && !codesSessionEnded && (
                    <p role="alert" className="mt-2 text-sm text-muted">
                      {t("account.codesAtRisk.retryFailed")}
                    </p>
                  )}
                </>
              )}
              {codesAtRiskHold && (
                <>
                  <button
                    type="button"
                    onClick={releaseCodesHold}
                    aria-describedby="codes-release-consequence-block"
                    className="mt-3 text-xs font-medium text-muted underline transition-colors hover:text-heading"
                  >
                    {t("account.mfaGate.continueWithoutCodes")}
                  </button>
                  <p id="codes-release-consequence-block" className="mt-1 text-xs text-muted">
                    {t("account.mfaGate.continueWithoutCodesConsequence")}
                  </p>
                </>
              )}
            </div>

            <div className="mt-8 space-y-6">
              <PasskeysSection />
              <TotpSection />
            </div>

            <p className="mt-8 text-center">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted transition-colors hover:text-heading"
              >
                <LogOut size={12} />
                {t("nav.logout")}
              </button>
            </p>

            <p className="mt-4 text-center text-xs text-muted">
              {t("account.mfaGate.deleteHint")}{" "}
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="font-medium text-danger transition-colors hover:text-danger/80"
              >
                {t("account.deleteAccount")}
              </button>
            </p>
          </div>
        </main>
      );
    }
  }

  return (
    <MfaRecoveryHoldContext.Provider value={reportHold}>
      <PasskeysSectionPresenceContext.Provider value={presenceStore}>
        {isAuthenticated ? (
          <div
            className={listUnconfirmed ? "hidden" : "contents"}
            hidden={listUnconfirmed}
            aria-hidden={listUnconfirmed || undefined}
          >
            {body}
            <EnrolOutcomeNotice
              placement="gate"
              hidden={displayBlock || passkeysSectionMounted || listUnconfirmed}
            />
            {codesCarrierVisible &&
              (codesUnchecked ? (
                <RecoveryCodesAtRiskBanner
                  state="unchecked"
                  announce={announceCarrier}
                  checking={codesCheckBusy}
                  retryFailed={carrierRetryFailed}
                  waitingForNetwork={listWaitingForNetwork}
                  announceWaiting={announceGateWaiting}
                  sessionEnded={codesSessionEnded}
                  onRetry={retryCodesCheck}
                  onSignIn={signInAgain}
                />
              ) : (
                <RecoveryCodesAtRiskBanner
                  state="loaded"
                  announce={announceCarrier}
                  unseen={data?.recoveryCodesUnseen === true}
                  onGoToAccount={() => void navigate({ to: "/account" })}
                  hideRelease={
                    pendingTotpCodes !== null ||
                    pendingPasskeyCodes !== null ||
                    data?.recoveryCodesUnseen === true
                  }
                  onRelease={() => {
                    const parked = [
                      pendingTotpRecoveryCodesKey,
                      pendingPasskeyRecoveryCodesKey,
                    ].some(
                      (key) => (queryClient.getQueryData<string[] | null>(key)?.length ?? 0) > 0,
                    );
                    if (parked) return;
                    releaseCodesHold();
                    if (data?.recoveryCodesUnseen !== true) {
                      setRecoveryCodesAtRisk(queryClient, false);
                    }
                  }}
                />
              ))}
          </div>
        ) : (
          body
        )}
        {isAuthenticated && (
          <p
            ref={regionRef}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          />
        )}
        {isAuthenticated && (
          <p aria-live="polite" aria-atomic="true" className="sr-only">
            {deadlineNoticeShown ? deadlineNotice : ""}
          </p>
        )}
        {isAuthenticated && listUnconfirmed && (
          <div className="fixed inset-0 z-[60] overflow-y-auto bg-void px-4 py-10">
            <div className="mx-auto max-w-md text-center">
              {listUnconfirmedError ? (
                <>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-warning/10">
                    <ShieldAlert size={28} className="text-warning" />
                  </div>
                  <h1
                    ref={unconfirmedHeadingRef}
                    tabIndex={-1}
                    className="mt-4 text-2xl font-bold text-heading outline-none"
                  >
                    {t("account.mfaGate.codesUnconfirmedTitle")}
                  </h1>
                  <p
                    key={
                      codesSessionEnded ? "codes-unconfirmed-session-ended" : "codes-unconfirmed"
                    }
                    id="codes-unconfirmed-body"
                    className="mt-2 text-sm text-muted"
                    role="alert"
                  >
                    {codesSessionEnded
                      ? t("account.mfaGate.codesSessionEndedBody")
                      : t("account.mfaGate.codesUnconfirmedBody")}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (codesSessionEnded) signInAgain();
                      else void checkListAgain("overlay");
                    }}
                    aria-describedby={
                      codesSessionEnded
                        ? "codes-unconfirmed-body"
                        : listWaitingForNetwork
                          ? "codes-unconfirmed-waiting"
                          : undefined
                    }
                    className="mt-6 inline-flex h-10 items-center justify-center rounded-lg bg-indigo px-6 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
                  >
                    {codesSessionEnded ? t("account.staleSession.action") : t("common.retry")}
                  </button>
                  {codesWaitingShown && (
                    <p id="codes-unconfirmed-waiting" className="mt-2 text-sm text-muted">
                      {t("common.waitingForNetwork")}
                    </p>
                  )}
                </>
              ) : listWaitingForNetwork ? (
                <p
                  key={announceOverlayWaiting ? "announced" : "quiet"}
                  role={announceOverlayWaiting ? "alert" : undefined}
                  className="mt-8 text-sm text-muted"
                >
                  {t("common.waitingForNetwork")}
                </p>
              ) : (
                <PageLoader className="mt-8" />
              )}
              <div className="mt-6 flex flex-col items-center gap-3">
                {!codesSessionEnded && (
                  <>
                    <button
                      type="button"
                      onClick={releaseCodesHold}
                      aria-describedby="codes-release-consequence-unconfirmed"
                      className="text-xs font-medium text-muted underline transition-colors hover:text-heading"
                    >
                      {t("account.mfaGate.continueWithoutCodes")}
                    </button>
                    <p id="codes-release-consequence-unconfirmed" className="text-xs text-muted">
                      {t("account.mfaGate.continueWithoutCodesConsequence")}
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-muted transition-colors hover:text-heading"
                >
                  <LogOut size={12} />
                  {t("nav.logout")}
                </button>
              </div>
            </div>
          </div>
        )}
        {isAuthenticated && displayBlock && (
          <DeleteAccountDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            withinMfaGate
          />
        )}
      </PasskeysSectionPresenceContext.Provider>
    </MfaRecoveryHoldContext.Provider>
  );
}
